//#region import libraries
import React, { memo, useEffect, useState } from 'react';

// bootstrap
import { Badge, Button, Form } from 'react-bootstrap';

// styling
import { Colors } from '_styles';

// icons
import {
    FaGripVertical, FaRegCopy, FaRegTrashCan, FaChevronDown, FaChevronRight, FaTriangleExclamation,
    FaXmark, FaPlus, FaRegComment, FaSliders, FaStar, FaRegCircleDot, FaListCheck, FaTableCells, FaCircleCheck,
} from 'react-icons/fa6';

// question types (single source of truth)
import { QUESTION_TYPES, typeLabel, typeNeedsOptions } from './survey-question-types';

//#endregion

const TYPE_ICONS = {
    single: FaRegCircleDot,
    multi: FaListCheck,
    scale: FaSliders,
    star: FaStar,
    grid: FaTableCells,
    text: FaRegComment,
};

const BORDER = '#e5e7eb';
const WARN = '#C0392B';
const MUTED = '#6b7280';
const STAR_CHOICES = [3, 4, 5, 6, 7, 8, 9, 10];

const labelStyle = { color: Colors.PRM_LIGHT_BLUE, fontWeight: 500, marginBottom: 6 };
const iconBtn = { background: 'none', border: 'none', padding: 6, color: MUTED, cursor: 'pointer', lineHeight: 1 };

//#region type picker
// Always visible; the chosen tile is highlighted in place so nothing jumps when you pick one.
// Border width is constant (2px) across states so selecting never shifts the layout.
const TypePicker = memo(({ value, onSelect }) => (
    <div role="radiogroup" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
        {QUESTION_TYPES.map((t) => {
            const Icon = TYPE_ICONS[t.icon] || FaRegComment;
            const selected = value === t.value;
            return (
                <button
                    key={t.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => onSelect(t.value)}
                    style={{
                        position: 'relative', display: 'flex', gap: 10, alignItems: 'flex-start', textAlign: 'left',
                        padding: '10px 30px 10px 12px', borderRadius: 8, cursor: 'pointer',
                        border: `2px solid ${selected ? Colors.PRM_LIGHT_BLUE : BORDER}`,
                        background: selected ? Colors.PRM_BLUE_ACCENT : '#fff',
                        boxShadow: selected ? `0 0 0 3px ${Colors.PRM_LIGHT_BLUE}22` : 'none',
                        transition: 'border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
                    }}
                >
                    <Icon size={16} color={selected ? Colors.PRM_LIGHT_BLUE : MUTED} style={{ marginTop: 3, flexShrink: 0 }} />
                    <span>
                        <span style={{ display: 'block', fontWeight: 600, fontSize: 14, color: selected ? Colors.PRM_LIGHT_BLUE : '#333' }}>{t.label}</span>
                        <span style={{ display: 'block', fontSize: 12, color: MUTED }}>{t.description}</span>
                    </span>
                    <FaCircleCheck
                        size={16}
                        color={Colors.PRM_LIGHT_BLUE}
                        style={{ position: 'absolute', top: 10, right: 10, opacity: selected ? 1 : 0, transition: 'opacity 120ms ease' }}
                    />
                </button>
            );
        })}
    </div>
));
//#endregion

/**
 * One editable question in the survey builder.
 *
 * @param question        draft question ({ _key, text:{lang}, type, options:{lang:[]}, ... })
 * @param index           zero-based position
 * @param languages       [{ label, value }]
 * @param expanded        whether the editor body is shown
 * @param validation      { messages, missingLangs } or undefined when valid
 * @param showErrors      only surface errors once the author has tried to save
 * @param dragHandleProps from react-beautiful-dnd
 * Callbacks receive the question `_key` so the parent can pass stable handlers.
 */
const QuestionCard = ({
    question, index, languages, expanded, onToggle, onChange, onDuplicate, onDelete,
    validation, showErrors, dragHandleProps, canDelete,
}) => {
    const [lang, setLang] = useState(languages[0]?.value || 'en');
    const [newOption, setNewOption] = useState('');

    // Keep the active language tab valid when languages change.
    useEffect(() => {
        if (!languages.some((l) => l.value === lang)) setLang(languages[0]?.value || 'en');
    }, [languages]);

    const codes = languages.map((l) => l.value);
    const primary = codes[0];
    const missingLangs = showErrors ? validation?.missingLangs || [] : [];
    const hasErrors = showErrors && validation?.messages?.length > 0;
    const isRtl = lang === 'ar';

    //#region updates (always return a new question object)
    const update = (patch) => onChange(question._key, { ...question, ...patch });

    const setText = (value) => update({ text: { ...question.text, [lang]: value } });

    const setType = (type) => {
        const patch = { type };
        if (type === 'rating' && !question.starsCount) patch.starsCount = 5;
        if (type === 'matrix-scale5') {
            if (!Array.isArray(question.items) || question.items.length === 0) {
                patch.items = [{ text: codes.reduce((a, c) => ({ ...a, [c]: '' }), {}) }];
            }
            if (!question.scaleLabels) {
                patch.scaleLabels = codes.reduce((a, c) => ({ ...a, [c]: ['', '', '', '', ''] }), {});
            }
        }
        update(patch);
    };

    // Options line up by index across languages, so add/remove touch every language.
    const addOption = () => {
        const value = newOption.trim();
        if (!value) return;
        const options = {};
        codes.forEach((c) => {
            options[c] = [...(question.options?.[c] || []), c === lang ? value : ''];
        });
        update({ options });
        setNewOption('');
    };

    const editOption = (i, value) => {
        const arr = [...(question.options?.[lang] || [])];
        arr[i] = value;
        update({ options: { ...question.options, [lang]: arr } });
    };

    const removeOption = (i) => {
        const options = {};
        codes.forEach((c) => {
            options[c] = (question.options?.[c] || []).filter((_, idx) => idx !== i);
        });
        update({ options });
    };

    const editScaleLabel = (i, value) => {
        const labels = [...(question.scaleLabels?.[lang] || ['', '', '', '', ''])];
        labels[i] = value;
        update({ scaleLabels: { ...question.scaleLabels, [lang]: labels } });
    };

    const addRow = () =>
        update({ items: [...(question.items || []), { text: codes.reduce((a, c) => ({ ...a, [c]: '' }), {}) }] });

    const editRow = (i, value) =>
        update({
            items: question.items.map((it, idx) => (idx === i ? { ...it, text: { ...it.text, [lang]: value } } : it)),
        });

    const removeRow = (i) => update({ items: question.items.filter((_, idx) => idx !== i) });
    //#endregion

    const previewText = question.text?.[primary]?.trim() || <span style={{ color: MUTED, fontStyle: 'italic' }}>Untitled question</span>;
    const langLabel = languages.find((l) => l.value === lang)?.label || lang;

    return (
        <div
            id={`question-card-${question._key}`}
            style={{
                border: `1px solid ${hasErrors ? WARN : expanded ? Colors.PRM_LIGHT_BLUE : BORDER}`,
                borderRadius: 10, background: '#fff', marginBottom: 12,
                boxShadow: expanded ? '0 2px 10px rgba(0,0,0,0.06)' : 'none',
            }}
        >
            {/* Header — always visible */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
                <span {...dragHandleProps} title="Drag to reorder" style={{ color: MUTED, cursor: 'grab', display: 'flex' }}>
                    <FaGripVertical />
                </span>

                <div onClick={() => onToggle(question._key)} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <span style={{ fontWeight: 600, color: Colors.PRM_LIGHT_BLUE, flexShrink: 0 }}>Q{index + 1}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#333' }}>{previewText}</span>
                    {question.type && <Badge bg="light" text="dark" style={{ border: `1px solid ${BORDER}`, fontWeight: 500, flexShrink: 0 }}>{typeLabel(question.type)}</Badge>}
                    {question.optional && <Badge bg="secondary" style={{ fontWeight: 500, flexShrink: 0 }}>Optional</Badge>}
                    {hasErrors && <FaTriangleExclamation color={WARN} title="This question needs attention" style={{ flexShrink: 0 }} />}
                </div>

                <button type="button" style={iconBtn} title="Duplicate question" onClick={() => onDuplicate(question._key)}><FaRegCopy /></button>
                {canDelete && (
                    <button type="button" style={{ ...iconBtn, color: WARN }} title="Delete question" onClick={() => onDelete(question._key)}><FaRegTrashCan /></button>
                )}
                <button type="button" style={iconBtn} title={expanded ? 'Collapse' : 'Expand'} onClick={() => onToggle(question._key)}>
                    {expanded ? <FaChevronDown /> : <FaChevronRight />}
                </button>
            </div>

            {expanded && (
                <div style={{ padding: '4px 16px 16px', borderTop: `1px solid ${BORDER}` }}>

                    {/* Language tabs */}
                    {languages.length > 1 && (
                        <div style={{ display: 'flex', gap: 4, margin: '12px 0 4px' }}>
                            {languages.map((l) => {
                                const active = l.value === lang;
                                const missing = missingLangs.includes(l.value);
                                return (
                                    <button
                                        key={l.value}
                                        type="button"
                                        onClick={() => setLang(l.value)}
                                        style={{
                                            padding: '4px 12px', borderRadius: 16, fontSize: 13, cursor: 'pointer',
                                            border: `1px solid ${active ? Colors.PRM_LIGHT_BLUE : BORDER}`,
                                            background: active ? Colors.PRM_LIGHT_BLUE : '#fff',
                                            color: active ? '#fff' : '#333',
                                        }}
                                    >
                                        {l.label}
                                        {missing && <span title="Missing text" style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: WARN, marginLeft: 6, verticalAlign: 'middle' }} />}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Question text */}
                    <Form.Group className="mt-3">
                        <Form.Label style={labelStyle}>Question{languages.length > 1 ? ` (${langLabel})` : ''}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            dir={isRtl ? 'rtl' : 'ltr'}
                            placeholder="e.g. How satisfied were you with the assistance you received?"
                            value={question.text?.[lang] || ''}
                            onChange={(e) => setText(e.target.value)}
                        />
                    </Form.Group>

                    {/* Answer type */}
                    <Form.Group className="mt-3">
                        <Form.Label style={labelStyle}>Answer type</Form.Label>
                        <TypePicker value={question.type} onSelect={setType} />
                    </Form.Group>

                    {/* Options */}
                    {typeNeedsOptions(question.type) && (
                        <Form.Group className="mt-3">
                            <Form.Label style={labelStyle}>Answer options{languages.length > 1 ? ` (${langLabel})` : ''}</Form.Label>
                            {(question.options?.[lang] || []).map((opt, i) => (
                                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                                    <span style={{ width: 20, color: MUTED, fontSize: 13, textAlign: 'right' }}>{i + 1}.</span>
                                    <Form.Control
                                        size="sm"
                                        dir={isRtl ? 'rtl' : 'ltr'}
                                        value={opt}
                                        isInvalid={showErrors && !String(opt).trim()}
                                        placeholder={`Translation for option ${i + 1}`}
                                        onChange={(e) => editOption(i, e.target.value)}
                                    />
                                    <button type="button" style={iconBtn} title="Remove option" onClick={() => removeOption(i)}><FaXmark /></button>
                                </div>
                            ))}
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <span style={{ width: 20 }} />
                                <Form.Control
                                    size="sm"
                                    dir={isRtl ? 'rtl' : 'ltr'}
                                    placeholder="Type an option and press Enter"
                                    value={newOption}
                                    onChange={(e) => setNewOption(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); addOption(); }
                                    }}
                                />
                                <Button size="sm" variant="outline-primary" onClick={addOption} disabled={!newOption.trim()}>
                                    <FaPlus /> Add
                                </Button>
                            </div>
                            {languages.length > 1 && (
                                <Form.Text muted>Options are shared across languages — switch tabs to translate each one.</Form.Text>
                            )}
                        </Form.Group>
                    )}

                    {/* Stars */}
                    {question.type === 'rating' && (
                        <Form.Group className="mt-3" style={{ maxWidth: 200 }}>
                            <Form.Label style={labelStyle}>Number of stars</Form.Label>
                            <Form.Select
                                size="sm"
                                value={question.starsCount || 5}
                                onChange={(e) => update({ starsCount: parseInt(e.target.value, 10) })}
                            >
                                {STAR_CHOICES.map((n) => <option key={n} value={n}>{n} stars</option>)}
                            </Form.Select>
                        </Form.Group>
                    )}

                    {/* Matrix */}
                    {question.type === 'matrix-scale5' && (
                        <>
                            <Form.Group className="mt-3">
                                <Form.Label style={labelStyle}>Items to rate{languages.length > 1 ? ` (${langLabel})` : ''}</Form.Label>
                                {(question.items || []).map((item, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                                        <span style={{ width: 20, color: MUTED, fontSize: 13, textAlign: 'right' }}>{i + 1}.</span>
                                        <Form.Control
                                            size="sm"
                                            dir={isRtl ? 'rtl' : 'ltr'}
                                            placeholder="e.g. Courtesy of staff"
                                            value={item.text?.[lang] || ''}
                                            isInvalid={showErrors && !String(item.text?.[lang] || '').trim()}
                                            onChange={(e) => editRow(i, e.target.value)}
                                        />
                                        <button type="button" style={iconBtn} title="Remove item" onClick={() => removeRow(i)}><FaXmark /></button>
                                    </div>
                                ))}
                                <Button size="sm" variant="outline-primary" onClick={addRow} style={{ marginLeft: 26 }}>
                                    <FaPlus /> Add item
                                </Button>
                            </Form.Group>

                            <Form.Group className="mt-3">
                                <Form.Label style={labelStyle}>Scale labels <span style={{ color: MUTED, fontWeight: 400 }}>(optional)</span></Form.Label>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    {[0, 1, 2, 3, 4].map((i) => (
                                        <Form.Control
                                            key={i}
                                            size="sm"
                                            dir={isRtl ? 'rtl' : 'ltr'}
                                            style={{ textAlign: 'center' }}
                                            placeholder={['Very poor', 'Poor', 'Average', 'Good', 'Excellent'][i]}
                                            value={question.scaleLabels?.[lang]?.[i] || ''}
                                            onChange={(e) => editScaleLabel(i, e.target.value)}
                                        />
                                    ))}
                                </div>
                                <Form.Text muted>Left blank, passengers see the numbers 1–5.</Form.Text>
                            </Form.Group>
                        </>
                    )}

                    {/* Optional */}
                    <Form.Check
                        className="mt-3"
                        type="switch"
                        id={`optional-switch-${question._key}`}
                        label="Optional — passengers can skip this question"
                        checked={question.optional || false}
                        onChange={(e) => update({ optional: e.target.checked })}
                    />

                    {/* Errors */}
                    {hasErrors && (
                        <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 6, background: '#FDECEA', color: WARN, fontSize: 13 }}>
                            {validation.messages.map((m) => <div key={m}>• {m}</div>)}
                            {languages.length > 1 && missingLangs.length > 0 && (
                                <div>• Check: {missingLangs.map((c) => languages.find((l) => l.value === c)?.label || c).join(', ')}</div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Only re-render the card being edited. dragHandleProps is a fresh object on every list
// render but its contents are stable, and validation is recomputed each keystroke, so
// compare those by value.
const sameValidation = (a, b) =>
    (a?.messages || []).join('|') === (b?.messages || []).join('|') &&
    (a?.missingLangs || []).join('|') === (b?.missingLangs || []).join('|');

const areEqual = (prev, next) =>
    prev.question === next.question &&
    prev.index === next.index &&
    prev.languages === next.languages &&
    prev.expanded === next.expanded &&
    prev.showErrors === next.showErrors &&
    prev.canDelete === next.canDelete &&
    prev.onChange === next.onChange &&
    prev.onToggle === next.onToggle &&
    prev.onDuplicate === next.onDuplicate &&
    prev.onDelete === next.onDelete &&
    sameValidation(prev.validation, next.validation);

export default memo(QuestionCard, areEqual);
